/**
 * The assembled Pitch (9-5-2) GameModule.
 *
 * Pitch is the smallest of the four games against the engine: no decisions,
 * no kitty, no dummy. Its one quirk is that trump is not named in the auction
 * — the pitcher's first lead makes that suit trump — which is `onCardPlayed`
 * writing `extra.trump` before the reducer builds the ranking for the trick.
 */
import { SUIT_LABEL, suitOf } from '../../core/cards.js';
import { PARTNERSHIP_4 } from '../../core/seats.js';
import { DEFAULT_PITCH_CONFIG, PITCH_TRICKS_PER_HAND, validatePitchConfig, } from './config.js';
import { pitchAuction, pitchContractLabel } from './auction.js';
import { pitchCurrentTrick, pitchDealPlan, pitchDeck, pitchLegalPlays, pitchProvesVoid, pitchRanking, pitchVisibility, } from './rules.js';
import { pitchMatchEnd, pitchScoreHand } from './scoring.js';
export const pitch = {
    meta: { id: 'pitch', name: 'Pitch', rotation: 'clockwise' },
    defaultConfig: DEFAULT_PITCH_CONFIG,
    validateConfig: validatePitchConfig,
    table: (_config) => PARTNERSHIP_4,
    tricksPerHand: (_config) => PITCH_TRICKS_PER_HAND,
    deck: pitchDeck,
    dealPlan: pitchDealPlan,
    auction: pitchAuction,
    ranking(hand, _config) {
        return pitchRanking(hand.extra);
    },
    legalPlays(hand, seat, _table, config) {
        return pitchLegalPlays(hand.secret.holdings[seat] ?? [], pitchCurrentTrick(hand), pitchRanking(hand.extra), config);
    },
    initHandExtra: () => ({ trump: null }),
    initMatchExtra: () => ({ lastBid: null }),
    onContract(hand, table) {
        return {
            extra: hand.extra,
            pending: null,
            activeSeats: Array.from({ length: table.seatCount }, (_, i) => i),
            // Rules §2.2.6: the pitcher leads, and that lead names trump.
            leader: hand.contract.pitcher,
        };
    },
    applyDecision() {
        return { ok: false, error: 'Pitch has no decisions' };
    },
    onCardPlayed(hand, _seat, card, _nominate) {
        if (hand.extra.trump !== null)
            return hand.extra;
        const trick = pitchCurrentTrick(hand);
        // The reducer calls this with the card already on the table, so the
        // hand's first lead is trick 0 with exactly one play.
        if (trick.index !== 0 || trick.plays.length !== 1)
            return hand.extra;
        return { ...hand.extra, trump: suitOf(card) };
    },
    provesVoid: pitchProvesVoid,
    nextLeader: (_hand, winner) => winner,
    scoreHand: pitchScoreHand,
    /** Only reachable with `stuckDealer` off: everybody passed, so redeal. */
    onPassedOut: () => ({ kind: 'redeal' }),
    matchEnd: (state, config) => pitchMatchEnd(state, config),
    roles(_hand, table) {
        const all = Array.from({ length: table.seatCount }, (_, i) => i);
        return { activeSeats: all, controllerOf: all };
    },
    visibility: pitchVisibility,
    contractLabel(contract, _config, extra) {
        if (!contract)
            return 'Passed out';
        const trump = extra?.trump ?? null;
        return trump === null ? pitchContractLabel(contract.bid) : `${contract.bid}${SUIT_LABEL[trump]}`;
    },
};
//# sourceMappingURL=module.js.map